<script setup lang="ts">
  /**
   * Switch — 03-components/20b-switch · OSCloud DS 1.1.151
   */
  type Size = 'default' | 'small'

  withDefaults(
    defineProps<{
      modelValue?: boolean
      label?: string
      /** Uppercase state word beside the track; both are needed to show it. */
      onLabel?: string
      offLabel?: string
      /** Neutral message under the control. */
      hint?: string
      /** Validation message; turns the message line red. */
      error?: string
      disabled?: boolean
      /**
       * `small` is for a switch that is one part of a line of UI — a view setting beside a page
       * title, a toolbar control. A settings form stays `default` however short the form is.
       */
      size?: Size
      /** Puts the state word and the track after the label; visual order only. */
      controlTrailing?: boolean
    }>(),
    {
      modelValue: false,
      label: '',
      onLabel: '',
      offLabel: '',
      hint: '',
      error: '',
      disabled: false,
      size: 'default',
      controlTrailing: false,
    },
  )

  defineEmits<{ 'update:modelValue': [value: boolean] }>()

  defineOptions({ inheritAttrs: false })
</script>

<template>
  <div class="form-field">
    <!-- Native checked is the switch state, so there is no aria-checked to keep in sync. -->
    <label class="switch" :class="{ 'switch--control-trailing': controlTrailing, 'switch--small': size === 'small' }">
      <input
        v-bind="$attrs"
        type="checkbox"
        role="switch"
        :checked="modelValue"
        :disabled="disabled"
        :aria-invalid="error ? 'true' : undefined"
        @change="$emit('update:modelValue', ($event.target as HTMLInputElement).checked)"
      />
      <span class="switch__track"><span class="switch__thumb" /></span>
      <span v-if="onLabel && offLabel" class="switch__state" :data-on="onLabel" :data-off="offLabel" />
      <span v-if="label" class="form-field__label form-field__label--plain">{{ label }}</span>
    </label>
    <span
      v-if="error || hint"
      class="form-field__message"
      :role="error ? 'alert' : undefined"
      :data-test-id="error ? 'field-error' : undefined"
    >
      {{ error || hint }}
    </span>
  </div>
</template>

<style scoped lang="scss">
  .form-field {
    display: flex;
    flex-direction: column;
    min-width: 0;

    &__label {
      font-family: var(--bf-font-display);
      font-size: 11px;
      font-weight: var(--bf-fw-bold);
      color: var(--bf-off-black);
      text-transform: uppercase;
      letter-spacing: var(--bf-tr-eyebrow);
    }

    /* Inside a control block the plain label is the text of one setting, not the name of a
       field, so it takes the regular weight Radio and Checkbox already share (DS 1.1.145).
       `font-family` is restated because the DS hangs it on the base `.form-field__label`,
       which these scoped styles never declare — without it the label falls through to the
       body font and renders ~2px narrower than the design. No `> .form-field__label` gap rule:
       the label sits inline with its control, not above it. */
    &__label--plain {
      font-family: var(--bf-font-display);
      font-size: var(--bf-fs-xs);
      font-weight: var(--bf-fw-regular);
      line-height: var(--bf-lh-normal);
      color: var(--bf-fg-1);
      text-transform: none;
      letter-spacing: var(--bf-tr-normal);
    }

    &__message {
      color: var(--bf-fg-2);
      font-size: var(--bf-fs-xs);
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: var(--bf-form-message-gap);
    }

    &__message[role='alert'],
    &:has([aria-invalid='true']) &__message {
      color: var(--bf-form-invalid-text);
    }
  }

  .switch {
    display: inline-flex;
    align-items: center;
    gap: var(--bf-sp-2);
    cursor: pointer;
    user-select: none;

    input {
      position: absolute;
      opacity: 0;
      pointer-events: none;
    }

    /* Control side. A switch is a setting - the label names the subject, the control
       shows its value - so it often belongs at the trailing edge. DOM order is fixed
       (input, __track, __state, label) because `input:checked + __track` reads an
       adjacent sibling; the modifier changes visual order only, so label association,
       tab order and every state rule keep working. `order`, not `row-reverse`. */
    &--control-trailing > &__track {
      order: 2;
    }

    &--control-trailing > &__state {
      order: 1;
    }

    &__track {
      position: relative;
      flex-shrink: 0;
      width: 38px;
      height: 22px;
      border-radius: var(--bf-r-pill);
      background: var(--bf-dusky-2);
      transition:
        background var(--bf-dur-base) var(--bf-ease),
        box-shadow var(--bf-dur-fast) var(--bf-ease);
    }

    /* 18px thumb inset 2px; checked, it travels 16px to the far end. */
    &__thumb {
      position: absolute;
      top: 2px;
      left: 2px;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: var(--bf-white);
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06); /* the control's own edge, not the surface ladder */
      transition: transform var(--bf-dur-base) var(--bf-ease);
    }

    input:checked + &__track {
      background: var(--bf-orange-dark);
    }

    input:checked + &__track &__thumb {
      transform: translateX(16px);
    }

    input:focus-visible + &__track {
      box-shadow: var(--bf-form-focus-ring);
    }

    &:has(input:disabled) {
      opacity: var(--bf-form-disabled-opacity);
      cursor: not-allowed;
    }

    /* Reserves the wider word's width so the label does not jump. */
    &__state {
      min-width: 2.6em;
      font-family: var(--bf-font-display);
      font-size: var(--bf-fs-xs_5);
      font-weight: var(--bf-fw-bold);
      letter-spacing: var(--bf-tr-caps-sm);
      text-transform: uppercase;
      color: var(--bf-fg-3);
      transition: color var(--bf-dur-base) var(--bf-ease);

      &::before {
        content: attr(data-off);
      }
    }

    input:checked ~ &__state {
      color: var(--bf-orange-dark);
    }

    input:checked ~ &__state::before {
      content: attr(data-on);
    }

    /* --small: the default recipe one step down, keeping its ratios — 32x18 track, 14px thumb
       at the same 2px inset, so the travel is 14px. Geometry only: gap, focus ring, disabled
       opacity, the order modifier and every state selector are untouched, and the label keeps
       the Form field's size because the switch sits in a line of body-size text. 32x18 is the
       floor — at 14px the thumb's edge shadow still separates it from an --orange-dark track.
       `__state` is deliberately not stepped down: this app's default is already --bf-fs-2xs,
       below the step upstream gives --small (see the known gap in the spec). */
    &--small > &__track {
      width: 32px;
      height: 18px;
    }

    &--small &__thumb {
      width: 14px;
      height: 14px;
    }

    &--small > &__state {
      font-size: var(--bf-fs-xs);
    }

    &--small input:checked + &__track &__thumb {
      transform: translateX(14px);
    }
  }
</style>
